/**
 * LLM-assisted backlog health narrative
 *
 * Takes the deterministic backlog health metrics (zombie counts, staleness
 * distributions, category breakdowns) and generates a plain-language
 * narrative interpretation. All deterministic calculation stays in the
 * BacklogHealth component; this service only adds the "what does this mean?"
 * layer.
 *
 * Registry: backlog_health_narrator — on-demand narrator. Transport: callFeatureLlm.
 * Advisory only — never mutates any item state.
 */

import { callFeatureLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

const BACKLOG_HEALTH_SYSTEM_PROMPT =
  'You analyze product backlog health and provide actionable narratives. Respond with valid JSON only.';

export interface ZombieSnapshot {
  title: string;
  daysSinceUpdate: number;
  category?: string;
  cycleCount: number;
}

export interface OverdueSnapshot {
  title: string;
  daysOverdue: number;
  category?: string;
}

export interface BacklogMetrics {
  totalActiveItems: number;
  zombieCount: number;
  overdueCount: number;
  stalenessThresholdDays: number;
  zombieSamples: ZombieSnapshot[];
  overdueSamples: OverdueSnapshot[];
  categoryBreakdown: Record<string, number>;
}

export interface BacklogHealthNarrative {
  headline: string;
  assessment: string;
  patterns: string[];
  topActions: string[];
  prognosis: string;
}

/**
 * Generate a plain-language health narrative for the current backlog state.
 */
export async function generateBacklogHealthNarrative(
  metrics: BacklogMetrics
): Promise<BacklogHealthNarrative | null> {
  if (!isAiCapabilityEnabled('backlog_health_narrator')) return null;

  const hasIssues = metrics.zombieCount > 0 || metrics.overdueCount > 0;
  if (!hasIssues) return null;

  try {
    const content = await callFeatureLlm({
      userPrompt: buildNarrativePrompt(metrics),
      systemPrompt: BACKLOG_HEALTH_SYSTEM_PROMPT,
      conversationId: 'backlog-health-' + Date.now(),
      model: DEFAULT_MODEL,
      temperature: 0.4,
      maxTokens: 600,
    });
    if (!content) return null;
    return parseNarrativeResponse(content);
  } catch (error) {
    console.warn('LLM backlog health narrative failed:', error);
    return null;
  }
}

function buildNarrativePrompt(metrics: BacklogMetrics): string {
  const zombieList = metrics.zombieSamples
    .slice(0, 10)
    .map(
      (z) =>
        `  - "${z.title}" — ${z.daysSinceUpdate} days stale${z.category ? `, category: ${z.category}` : ''}${z.cycleCount === 0 ? ', never in a cycle' : `, in ${z.cycleCount} past cycles`}`
    )
    .join('\n');

  const overdueList = metrics.overdueSamples
    .slice(0, 10)
    .map(
      (o) =>
        `  - "${o.title}" — ${o.daysOverdue} days past review date${o.category ? `, category: ${o.category}` : ''}`
    )
    .join('\n');

  const categoryLines = Object.entries(metrics.categoryBreakdown)
    .sort(([, a], [, b]) => b - a)
    .map(([cat, count]) => `  - ${cat}: ${count} unhealthy items`)
    .join('\n');

  return `You are a prioritization coach analyzing the health of a product backlog.

Backlog summary:
- Total active items: ${metrics.totalActiveItems}
- Zombie priorities (stale ${metrics.stalenessThresholdDays}+ days, no recent progress): ${metrics.zombieCount}
- Overdue deferrals (past their review date): ${metrics.overdueCount}

Sample zombie priorities:
${zombieList || '  (none in sample)'}

Sample overdue deferrals:
${overdueList || '  (none in sample)'}

${Object.keys(metrics.categoryBreakdown).length > 0 ? `Category breakdown of unhealthy items:\n${categoryLines}` : ''}

Your task: Write a concise, actionable health narrative that helps the team understand what these patterns mean and what they should do about them. Be specific and direct — this team knows prioritization.

Return JSON only, no markdown or extra text:
{
  "headline": "One sentence assessment (e.g. '23% of your backlog is stagnant — cleanup is overdue')",
  "assessment": "2-3 sentences explaining what the patterns indicate about this backlog's health",
  "patterns": ["2-3 specific patterns you see in the data (bullet-point level strings)"],
  "topActions": ["2-3 specific, actionable recommended steps (strings)"],
  "prognosis": "One sentence on what will happen if nothing is done"
}`;
}

function parseNarrativeResponse(content: string): BacklogHealthNarrative | null {
  try {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;

    const parsed = JSON.parse(jsonMatch[0]);
    if (typeof parsed.headline !== 'string') return null;

    return {
      headline: parsed.headline.trim(),
      assessment:
        typeof parsed.assessment === 'string' ? parsed.assessment.trim() : '',
      patterns: Array.isArray(parsed.patterns)
        ? parsed.patterns.filter((p: unknown) => typeof p === 'string').slice(0, 4)
        : [],
      topActions: Array.isArray(parsed.topActions)
        ? parsed.topActions.filter((a: unknown) => typeof a === 'string').slice(0, 4)
        : [],
      prognosis:
        typeof parsed.prognosis === 'string' ? parsed.prognosis.trim() : '',
    };
  } catch (e) {
    console.warn('Parse backlog health narrative failed:', e);
    return null;
  }
}
